'use client'

import { useEffect, useState } from "react";
import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { ReservationFormProps } from "@/types/popadvise";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/system/radio";
import { SYSTEM_BE } from "@/lib/constants";
import { useCurrentBranch } from "@/context/current-branch-provider";
import { toast } from "@/hooks/use-toast";
import Loading from "@/components/system/loading";
import { CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

function transformHour(hour: string): ReservationFormProps["hour"] {
    return `${hour.slice(0, 2)}:${hour.slice(2)}` as ReservationFormProps["hour"]
}

export function ReservationsHours({ onContinue, onBackStep, userData, setUserData }: {
    onContinue: () => void
    onBackStep: () => void
    userData: ReservationFormProps
    setUserData: (userData: ReservationFormProps) => void
}) {

    const { currentBranch } = useCurrentBranch()
    const [schedule, setSchedule] = useState([])
    const [loading, setLoading] = useState(false)

    const formSchema = z.object({
        hour: z.string().min(5, { message: "Debes elegir una hora para la reservación." }),
    })

    type FormSchemaType = z.infer<typeof formSchema>

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            hour: userData.hour ? transformHour(userData.hour) : '',
        }
    })

    const handleOnSubmit = async (data: FormSchemaType) => {
        onContinue()
        setUserData({
            ...userData,
            hour: data.hour
        })
    }

    useEffect(() => {
        const date = new Date(userData.date)
        setLoading(true)

        fetch(`${SYSTEM_BE}/v1/branch-schedules/daily?selected_date=${date.toISOString().slice(0, 10)}&day_of_week=${date.getDay() + 1}&bid=${currentBranch.id}`)
            .then(response => {
                if (!response.ok) {
                    throw new Error("No se pudo cargar el horario")
                }
                return response.json()
            })
            .then(data => {
                setSchedule(data.availableTimes)
            })
            .catch(() => {
                toast({ title: "Error", description: "Ocurrió un error al cargar el horario", variant: "destructive" })
            })
            .finally(() => setLoading(false))
    }, [currentBranch.id, userData.date])

    return (
        <div className="p-6 pt-8">
            <CardHeader className="px-0">
                <CardTitle className="text-left text-xl">{currentBranch.name}</CardTitle>
                <CardDescription className="text-[#6C6C6C] text-left">
                    Escoge una hora
                </CardDescription>
            </CardHeader>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleOnSubmit)}>
                    {
                        loading && (
                            <div className="flex justify-center items-center">
                                <Loading />
                            </div>
                        )
                    }
                    {
                        !loading && (
                            <FormField
                                control={form.control}
                                name="hour"
                                render={({ field }) => (
                                    <FormItem className="col-span-2 md:col-span-3 flex flex-col gap-1">
                                        <FormControl >
                                            <RadioGroup
                                                onValueChange={field.onChange}
                                                defaultValue={field.value}
                                                className="grid grid-cols-3 gap-3"
                                            >
                                                {
                                                    schedule.map((hour) => <HourCard
                                                        key={hour}
                                                        hour={hour}
                                                        format=""
                                                        disabled={false}
                                                        selected={field.value === hour || userData.hour === hour}
                                                    />)
                                                }
                                            </RadioGroup>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )
                    }
                    <footer className="grid grid-cols-1 gap-3 mt-5">
                        <Button
                            type="submit"
                            className={`
                            py-6 rounded-lg w-full font-semibold
                            ${false && 'bg-[#F2F4F7] text-[#A0A9B4]'}    
                        `}
                            disabled={false}
                        >Continuar</Button>
                        <Button
                            type="submit"
                            className="py-6 rounded-lg w-full font-semibold m-0 text-[#6C6C6C]"
                            onClick={() => onBackStep()}
                            variant="ghost"
                        >Atras</Button>
                    </footer>
                </form>
            </FormProvider>
        </div>
    )
}

function HourCard({ hour, format, disabled, selected }: {
    hour: string
    format: string
    disabled: boolean
    selected: boolean
}) {
    return (
        <FormItem>
            <FormLabel
                htmlFor={`hour-${hour}`}
                className={`
                    w-full flex items-center justify-center gap-2 rounded-lg p-4 border-2 border-slate-200 font-semibold transition-colors text-black dark:text-white
                    ${disabled ? 'bg-[#F6F8FA] text-[#CDD0D5] cursor-not-allowed opacity-50' : ''}
                    ${!selected && !disabled ? 'hover:border-slate-900 cursor-pointer' : ''}
                    ${selected ? 'bg-white border-slate-900 cursor-pointer dark:bg-gray-900 dark:border-gray-700' : ''}
                `}
            >
                <div className="text-center text-sm font-semibold">{hour}</div>
                <div className="text-center text-sm font-semibold text-[#CDD0D5]">{format}</div>
            </FormLabel>
            <FormControl>
                <RadioGroupItem id={`hour-${hour}`} value={hour} className="sr-only" />
            </FormControl>
        </FormItem>
    )
}